import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Layout from './components/layout/Layout';
import { GolfAuthLoadingScreen } from '@golfauth/ui';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import AuthStateHandler from './components/auth/AuthStateHandler';
import { ProDeepLinkHandler } from './components/ProDeepLinkHandler';
import ProtectedRoute from './components/auth/ProtectedRoute';
import GolferProtectedRoute from './components/auth/GolferProtectedRoute';
import { EmailGateProvider } from './context/EmailGateContext';
import PWAManifestSwapper from './components/PWAManifestSwapper';
import { LegacyProPathGuard, legacyProRedirectRoutes } from './routing/legacyProRedirects';
import { InstallPrompt } from '@golfauth/ui';

// Wrap the InstallPrompt so it reads the auth store and triggers on
// first sign-in (customer OR professional). All suppression logic
// (native shell, already-installed, desktop, cooldown) lives inside
// InstallPrompt itself.
function InstallPromptOnSignIn() {
  const { customer, professional } = useAuthStore();
  const isPro = !!professional;
  const storageKey = isPro
    ? 'golfauth-fitting-pro-install-prompt'
    : 'golfauth-fitting-golfer-install-prompt';
  const appName = isPro ? 'GolfAuth Pro' : 'GolfAuth';
  return (
    <InstallPrompt
      enabled={!!customer || !!professional}
      appName={appName}
      storageKey={storageKey}
    />
  );
}
import { useAuthStore } from './store/authStore';

/**
 * Module-scope native check.
 *
 * The Pro Capacitor wrap (com.golfauth.pro) is pro-exclusive — golfer
 * marketing must never be the entry surface. We use this constant to
 * swap the `/` route to a router-level guard (NativeEntry) instead of
 * the marketing HomePage, so the marketing route literally cannot
 * render in the native shell regardless of render/effect timing.
 *
 * Returns false in the web bundle, so web behaviour is untouched.
 *
 * Diagnostic: a `[Pro app] native shell detected, entry = …` line in
 * the WKWebView console (Xcode → Debug area) on first launch confirms
 * the check is firing. If you see marketing copy in the iOS app and
 * NO such log line, the running build is stale — clean Xcode, delete
 * the app from the simulator/device, and run again.
 */
const IS_NATIVE = Capacitor.isNativePlatform();
if (IS_NATIVE && typeof console !== 'undefined') {
  console.log('[Pro app] native shell detected, entry routed to /login (or /pro/admin if signed in)');
}

// Eager — landing page + most-common entry. Anything not in this short
// list is loaded on demand by React.lazy() so it doesn't bloat the main
// bundle. Side effect: lib/stripe and @stripe/stripe-js (only reached via
// admin routes → lib/stripe → loadStripe at module top) drop out of the
// main chunk entirely.
import HomePage from './pages/HomePage';
// D8: lazy so the marketing homepage doesn't ship LoginPage's 1.4 KiB
// of code in the eager bundle. Visitors clicking "Sign in" hit the
// lazy chunk on-demand; everyone else never pays for it.
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));

// Lazy — every other route. Each becomes its own chunk.
const ClubFittingPage = lazy(() => import('./pages/ClubFittingPage'));
const ClubConfigurationPage = lazy(() => import('./pages/ClubConfigurationPage'));
const SavedConfigurationsPage = lazy(() => import('./pages/SavedConfigurationsPage'));
const CreateOfferPage = lazy(() => import('./pages/CreateOfferPage'));
const CustomerOfferPage = lazy(() => import('./pages/CustomerOfferPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutConfirmationPage = lazy(() => import('./pages/CheckoutConfirmationPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AccountLockedPage = lazy(() => import('./pages/admin/AccountLockedPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ApprovalRequiredPage = lazy(() => import('./pages/auth/ApprovalRequiredPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const SetPasswordPage = lazy(() => import('./pages/auth/SetPasswordPage'));
const AccountVerifiedPage = lazy(() => import('./pages/auth/AccountVerifiedPage'));
const CustomerLogin = lazy(() => import('./pages/auth/CustomerLogin'));
const CustomerRegister = lazy(() => import('./pages/auth/CustomerRegister'));
const FindFitterPage = lazy(() => import('./pages/FindFitterPage'));
const SelectGolferPage = lazy(() => import('./pages/SelectGolferPage'));
const OfferLoginPage = lazy(() => import('./pages/OfferLoginPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// Preview-only routes (design wireframes; not linked from navigation).
const RoastCardPreview = lazy(() => import('./pages/preview/RoastCardPreview'));
/** Public bag share card — golfauth.com/bag/{handle} */
const PublicBagPage = lazy(() => import('./pages/PublicBagPage'));
const BagCardPreview = lazy(() => import('./pages/preview/BagCardPreview'));
// AEO published-statistics page — canonical citable source.
const StatsPage = lazy(() => import('./pages/StatsPage'));
// AEO Phase 2 — definitional content pages for AI citation share.
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const Verification101Page = lazy(() => import('./pages/Verification101Page'));
const GolferDashboard = lazy(() => import('./pages/golfer/GolferDashboard'));
const GolferFittingsPage = lazy(() => import('./pages/golfer/GolferFittingsPage'));
const GolferCorePage = lazy(() => import('./pages/golfer/GolferCorePage'));
const GolferProfile = lazy(() => import('./pages/golfer/GolferProfile'));
const GolferConfigHistory = lazy(() => import('./pages/golfer/GolferConfigHistory'));
const GolferCurrentQuotes = lazy(() => import('./pages/golfer/GolferCurrentQuotes'));
const GolferOrders = lazy(() => import('./pages/golfer/GolferOrders'));
const GolferMessages = lazy(() => import('./pages/golfer/GolferMessages'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const ProPage = lazy(() => import('./pages/ProPage'));
const FoundingFittersApplyPage = lazy(() => import('./pages/FoundingFittersApplyPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const DataRetentionPolicyPage = lazy(() => import('./pages/DataRetentionPolicyPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const OemSerialCheckDocsPage = lazy(() => import('./pages/developers/OemSerialCheckDocsPage'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const SubscriptionRequiredPage = lazy(() => import('./pages/SubscriptionRequiredPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));

/** Old path; preserves ?code= and other query params. */
function LegacyFindProfessionalRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/find-professional${search}`} replace />;
}

/**
 * Suspense fallback rendered while a lazy-loaded route chunk is in
 * flight. Suspense REPLACES the entire <Routes> tree (including
 * <Layout/>) until the chunk resolves — so this renders outside the
 * Layout flex chain. Pass min-h-screen so the shared loading screen
 * fills the viewport instead of collapsing to its content height.
 * Without this the page flashed a small 40vh-anchored spinner before
 * Layout returned with the route's own GolfAuthLoadingScreen,
 * producing a "section then page" loader sequence on every
 * cross-route navigation.
 */
function RouteFallback() {
  return <GolfAuthLoadingScreen className="min-h-screen" />;
}

/**
 * Native `/` entry. The Pro Capacitor wrap renders this in place of the
 * golfer-first HomePage. Decides between /pro/login and /pro/admin
 * once the auth store has bootstrapped; never paints marketing content.
 */
function NativeEntry() {
  const { professional, isInitialized } = useAuthStore();
  if (!isInitialized) return <RouteFallback />;
  return <Navigate to={professional ? '/pro/admin' : '/pro/login'} replace />;
}

/**
 * Wrap any route element that should NOT be reachable in the Pro
 * Capacitor app. On web, renders the children unchanged. On native,
 * silently redirects to /login (or a caller-specified target).
 *
 * Used to neutralise the golfer-facing routes (`/customer/*`, `/golfer/*`,
 * `/find-professional`, `/about`, `/team`, `/contact`, `/pro`, `/privacy`,
 * `/terms`, `/security`, `/cookies`, `/delete-account`) in the native shell, while leaving them
 * fully intact on the web build served at the apex `golfauth.com` (previously

 * `fitting.golfauth.com`, subdomain retired). Direct-URL or deep-link
 * access to any of those paths inside the native app lands on /login
 * instead of the golfer/marketing content.
 */
function NativeBlock({ children, to = '/pro/login' }: { children: React.ReactNode; to?: string }) {
  if (IS_NATIVE) return <Navigate to={to} replace />;
  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <AuthStateHandler />
      <ProDeepLinkHandler />
      <PWAManifestSwapper />
      <InstallPromptOnSignIn />
      <LegacyProPathGuard />
      <EmailGateProvider>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Public routes.
              IS_NATIVE swap: the Pro Capacitor wrap takes the index
              route to NativeEntry — a router-level guard that routes
              to /login or /select-golfer without ever instantiating
              the marketing HomePage. Web continues to render HomePage. */}
          <Route index element={IS_NATIVE ? <NativeEntry /> : <HomePage />} />

          {/* Golfer-facing marketing + audience-discovery routes.
              NativeBlock makes these inaccessible in the Pro Capacitor
              wrap — direct URL access lands on /login. Web behaviour
              unchanged. */}
          <Route path="find-professional" element={<NativeBlock><FindFitterPage /></NativeBlock>} />
          <Route path="find-fitter" element={<NativeBlock><LegacyFindProfessionalRedirect /></NativeBlock>} />
          {/* Contact is reachable on native too — the admin Settings tab
              opens it via a "Send us a message" CTA. ContactPage already
              has a mailto fallback for environments without form transport. */}
          <Route path="contact" element={<ContactPage />} />
          <Route path="about" element={<NativeBlock><AboutPage /></NativeBlock>} />
          <Route path="team" element={<NativeBlock><TeamPage /></NativeBlock>} />
          <Route path="pro" element={<NativeBlock><ProPage /></NativeBlock>} />
          {/* Preview wireframes — share with the team via direct link. */}
          <Route path="preview/roast-card" element={<NativeBlock><RoastCardPreview /></NativeBlock>} />
          <Route path="preview/bag-card" element={<NativeBlock><BagCardPreview /></NativeBlock>} />
          <Route path="bag/:handle" element={<NativeBlock><PublicBagPage /></NativeBlock>} />
          <Route path="stats" element={<NativeBlock><StatsPage /></NativeBlock>} />
          <Route path="glossary" element={<NativeBlock><GlossaryPage /></NativeBlock>} />
          <Route path="verification-101" element={<NativeBlock><Verification101Page /></NativeBlock>} />
          <Route path="pro/founding" element={<NativeBlock><FoundingFittersApplyPage /></NativeBlock>} />
          <Route path="pro/founding-fitters/apply" element={<NativeBlock><FoundingFittersApplyPage /></NativeBlock>} />
          <Route path="privacy" element={<NativeBlock><PrivacyPolicyPage /></NativeBlock>} />
          <Route path="privacy/data-retention" element={<NativeBlock><DataRetentionPolicyPage /></NativeBlock>} />
          <Route path="terms" element={<NativeBlock><TermsOfServicePage /></NativeBlock>} />
          <Route path="security" element={<NativeBlock><SecurityPage /></NativeBlock>} />
          <Route path="cookies" element={<NativeBlock><CookiePolicyPage /></NativeBlock>} />
          <Route path="delete-account" element={<NativeBlock><DeleteAccountPage /></NativeBlock>} />
          {/* Unlisted manufacturer API docs: handed to OEMs directly, never
              linked, not in marketing-routes.mjs (no prerender / sitemap),
              noindex in-page and via the Worker's X-Robots-Tag. */}
          <Route path="developers/oem" element={<NativeBlock><OemSerialCheckDocsPage /></NativeBlock>} />

          {/* Auth routes. All Pro auth lives under /pro/* — the apex
              golfauth.com is consumer-facing, so /login is reserved for
              future golfer-first auth if needed. Customer routes stay
              on /customer/* for the golfer audience. */}
          <Route path="pro/login" element={<LoginPage />} />
          <Route path="pro/register" element={<RegisterPage />} />
          <Route path="pro/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="pro/reset-password" element={<ResetPasswordPage />} />
          <Route path="pro/set-password" element={<SetPasswordPage />} />
          {/* Golfer auth — blocked in the Pro app. */}
          <Route path="customer/login" element={<NativeBlock><CustomerLogin /></NativeBlock>} />
          <Route path="customer/register" element={<NativeBlock><CustomerRegister /></NativeBlock>} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="account-verified" element={<AccountVerifiedPage />} />

          {/* Offer routes (public with code) — stay unprefixed since
              they're golfer-facing magic-links from Pro-issued offers. */}
          <Route path="offer/:code" element={<OfferLoginPage />} />
          <Route path="checkout/:code" element={<CheckoutPage />} />

          {/* Subscription routes — Pro-only, nested under /pro/*. */}
          <Route path="pro/subscription-required" element={<ProtectedRoute><SubscriptionRequiredPage /></ProtectedRoute>} />
          <Route path="pro/subscription-success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
          <Route path="pro/approval-required" element={<ProtectedRoute><ApprovalRequiredPage /></ProtectedRoute>} />

          {/* Professional protected routes — all nested under /pro/*. */}
          <Route path="pro/select-golfer" element={<ProtectedRoute><SelectGolferPage /></ProtectedRoute>} />
          <Route path="pro/fitting" element={<ProtectedRoute><ClubFittingPage /></ProtectedRoute>} />
          <Route path="pro/fitting/configure" element={<ProtectedRoute><ClubConfigurationPage /></ProtectedRoute>} />
          <Route path="pro/configurations" element={<ProtectedRoute><SavedConfigurationsPage /></ProtectedRoute>} />
          <Route path="pro/saved-configurations" element={<ProtectedRoute><SavedConfigurationsPage /></ProtectedRoute>} />
          <Route path="pro/create-offer" element={<ProtectedRoute><CreateOfferPage /></ProtectedRoute>} />
          <Route path="pro/customer-offer" element={<ProtectedRoute><CustomerOfferPage /></ProtectedRoute>} />
          <Route path="pro/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
          <Route path="pro/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="pro/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          {/* Public: golfers return here after Stripe 3DS; must not require professional session.
              Stays at /checkout/confirmation (no /pro prefix) so the Stripe return URL
              configured at intent-creation time still resolves. */}
          <Route path="checkout/confirmation" element={<CheckoutConfirmationPage />} />
          <Route path="pro/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="pro/account-locked" element={<ProtectedRoute><AccountLockedPage /></ProtectedRoute>} />

          {/* Golfer protected routes — wrapped in NativeBlock so the Pro
              Capacitor app cannot land on golfer surfaces even via a
              deep link. Web behaviour unchanged. */}
          {/* /golfer used to render GolferLandingPage (Fitting-branded
              spinner that immediately redirected to Core). Removed
              2026-07-02 — direct callers now invoke goToCore() themselves
              (HomePage PWA-launch effect, CustomerLogin post-login).
              Old bookmarks fall to the SPA catch-all below. */}
          <Route path="golfer/core" element={<NativeBlock><GolferProtectedRoute><GolferCorePage /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/fittings" element={<NativeBlock><GolferProtectedRoute><GolferFittingsPage /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/dashboard" element={<NativeBlock><GolferProtectedRoute><GolferDashboard /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/profile" element={<NativeBlock><GolferProtectedRoute><GolferProfile /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/config-history" element={<NativeBlock><GolferProtectedRoute><GolferConfigHistory /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/current-quotes" element={<NativeBlock><GolferProtectedRoute><GolferCurrentQuotes /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/orders" element={<NativeBlock><GolferProtectedRoute><GolferOrders /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/messages" element={<NativeBlock><GolferProtectedRoute><GolferMessages /></GolferProtectedRoute></NativeBlock>} />
          <Route path="golfer/messages/:threadId" element={<NativeBlock><GolferProtectedRoute><GolferMessages /></GolferProtectedRoute></NativeBlock>} />

          {/* Legacy /login → /pro/login etc. when edge 301s do not run (Cloudflare). */}
          {legacyProRedirectRoutes()}

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </Suspense>
      </EmailGateProvider>
    </ErrorBoundary>
  );
}

export default App;